// src/components/vault/SendDocumentDialog.jsx
import React from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Stack, TextField, Button, CircularProgress, Typography,
    Checkbox, FormControlLabel, MenuItem, IconButton, Box
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import themeColors from "../../theme";

const EXPIRY_OPTIONS = [
    { value: 1, label: "1 day" },
    { value: 3, label: "3 days" },
    { value: 7, label: "7 days" },
    { value: 14, label: "14 days" },
    { value: 30, label: "30 days" },
];

export default function SendDocumentDialog({ open, doc, onClose, onSubmit }) {
    const [recipientEmail, setRecipientEmail] = React.useState("");
    const [message, setMessage] = React.useState("");
    const [expiresInDays, setExpiresInDays] = React.useState(7);
    const [requireVerification, setRequireVerification] = React.useState(true);
    const [sending, setSending] = React.useState(false);
    const [error, setError] = React.useState("");

    const brand = themeColors?.teal || "#007F7E";

    React.useEffect(() => {
        if (open) {
            setRecipientEmail("");
            setMessage("");
            setExpiresInDays(7);
            setRequireVerification(true);
            setSending(false);
            setError("");
        }
    }, [open]);

    const isValidEmail = (email) => {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    };

    const handleSend = async () => {
        if (!recipientEmail.trim()) {
            setError("Recipient email is required");
            return;
        }
        if (!isValidEmail(recipientEmail.trim())) {
            setError("Please enter a valid email address");
            return;
        }
        setError("");
        setSending(true);
        try {
            await onSubmit?.({
                docId: doc?.id,
                recipientEmail: recipientEmail.trim(),
                message: message.trim() || null,
                encrypted: true,
                expiresInDays,
                requireVerification
            });
        } catch (err) {
            setError(err.message || "Failed to send document");
            setSending(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={sending ? undefined : onClose}
            maxWidth="xs"
            fullWidth
            PaperProps={{
                sx: { borderRadius: 2 }
            }}
        >
            <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}>
                <Typography variant="h6" fontWeight={600}>Send Document Securely</Typography>
                <IconButton onClick={onClose} disabled={sending} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2.5}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "text.secondary" }}>
                        <LockOutlinedIcon fontSize="small" />
                        <Typography variant="body2">
                            You are securely sending <strong>{doc?.name || "document"}</strong>
                        </Typography>
                    </Box>

                    <TextField
                        label="Recipient Email"
                        size="small"
                        type="email"
                        value={recipientEmail}
                        onChange={(e) => setRecipientEmail(e.target.value)}
                        disabled={sending}
                        placeholder="credentialing@hospital.org"
                        fullWidth
                        error={!!error && error.includes("email")}
                    />

                    <TextField
                        label="Optional Message"
                        size="small"
                        multiline
                        rows={2}
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        disabled={sending}
                        placeholder="Add a short note (optional)"
                        fullWidth
                    />

                    <Stack spacing={0.5}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={expiresInDays > 0}
                                        onChange={(e) => setExpiresInDays(e.target.checked ? 7 : 0)}
                                        disabled={sending}
                                        sx={{ color: brand, "&.Mui-checked": { color: brand } }}
                                    />
                                }
                                label={
                                    <Typography variant="body2">Link expires in</Typography>
                                }
                            />
                            <TextField
                                select
                                size="small"
                                value={expiresInDays || 7}
                                onChange={(e) => setExpiresInDays(Number(e.target.value))}
                                disabled={sending || expiresInDays === 0}
                                sx={{ minWidth: 100 }}
                            >
                                {EXPIRY_OPTIONS.map((opt) => (
                                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                ))}
                            </TextField>
                        </Stack>

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={requireVerification}
                                    onChange={(e) => setRequireVerification(e.target.checked)}
                                    disabled={sending}
                                    sx={{ color: brand, "&.Mui-checked": { color: brand } }}
                                />
                            }
                            label={
                                <Typography variant="body2">Recipient must verify email before download</Typography>
                            }
                        />
                    </Stack>

                    {error && (
                        <Typography variant="body2" color="error">{error}</Typography>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose} disabled={sending}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSend}
                    disabled={!recipientEmail.trim() || sending}
                    sx={{
                        backgroundColor: brand,
                        "&:hover": { backgroundColor: themeColors?.darkTeal || brand },
                        textTransform: "none",
                        fontWeight: 600,
                        px: 3
                    }}
                >
                    {sending ? <CircularProgress size={18} sx={{ color: "white" }} /> : "Send Securely"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
